import React, { useState, useRef, useEffect } from "react";
import {
  Chart as ChartJS,
  DoughnutController,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Doughnut } from "react-chartjs-2";

ChartJS.register(
  DoughnutController,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend
);

// ─────────────────────────────────────────────────────────────
// 🎨 COLORS
// ─────────────────────────────────────────────────────────────
const LOCATION_COLORS = [
  "#1babab",
  "#169999",
  "#148888",
  "#117777",
  "#0f6666",
  "#0c5555",
  "#0a4444",
  "#083333",
  "#062222",
  "#041111",
];

// ─────────────────────────────────────────────────────────────
// 🔧 HELPERS
// ─────────────────────────────────────────────────────────────
function fmtCompact(n) {
  if (!n && n !== 0) return "0";
  const num = Number(n);
  if (isNaN(num)) return "0";
  return new Intl.NumberFormat("en-IN", { notation: "compact" }).format(num);
}

function sliceRowsByTopN(rows, topN, byType) {
  const n = parseInt(topN, 10);
  if (isNaN(n) || n <= 0) return rows;
  const key = byType === "Amount" ? "sellingPrice" : "qty";
  return [...rows]
    .sort((a, b) => (Number(b?.[key]) || 0) - (Number(a?.[key]) || 0))
    .slice(0, n);
}

function TopNDropdown({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const choices = [10, 20, 30].map((n) => ({ key: String(n), label: `Top ${n}` }));
  const selected = choices.find((c) => c.key === value) || choices[0];

  return (
    <div style={styles.topNWrapper} ref={ref}>
      <div style={styles.topNBtn} onClick={() => setOpen(!open)}>
        {selected.label}
        <span style={styles.topNChevron}>▾</span>
      </div>
      {open && (
        <div style={styles.topNMenu}>
          {choices.map((c) => (
            <div
              key={c.key}
              style={{
                ...styles.topNOption,
                background: value === c.key ? "#eef2f3" : "#fff",
                fontWeight: value === c.key ? 600 : 400,
              }}
              onClick={() => {
                onChange(c.key);
                setOpen(false);
              }}
            >
              {c.label}
              {value === c.key && (
                <span style={{ color: "#1D9E75", marginLeft: "auto" }}>✓</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// 🔘 BY AMOUNT/QTY DROPDOWN (top-left badge style like image 3)
// ─────────────────────────────────────────────────────────────
function ByTypeDropdown({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  return (
    <div style={styles.byTypeWrapper} ref={ref}>
      <div style={styles.byTypeBtn} onClick={() => setOpen(!open)}>
        By {value}
        <span style={styles.byTypeChevron}>▾</span>
      </div>
      {open && (
        <div style={styles.byTypeMenu}>
          {["Amount", "Qty"].map((item) => (
            <div
              key={item}
              style={{
                ...styles.byTypeOption,
                background: value === item ? "#f0faf6" : "#fff",
                fontWeight: value === item ? 600 : 400,
              }}
              onClick={() => {
                onChange(item);
                setOpen(false);
              }}
            >
              {item}
              {value === item && (
                <span style={{ color: "#1D9E75", marginLeft: "auto" }}>✓</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// 🔘 THREE DOT MENU — Print icon + Download icon only (like image 2)
// ─────────────────────────────────────────────────────────────
function ThreeDotsMenu({ chartRef }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const handlePrint = () => {
    window.print();
    setOpen(false);
  };

  const handleDownload = () => {
    const chart = chartRef?.current;
    if (!chart) return;
    const url = chart.toBase64Image();
    const link = document.createElement("a");
    link.href = url;
    link.download = "chart.png";
    link.click();
    setOpen(false);
  };

  // SVG icons matching image 2 style
  const PrintIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6 9 6 2 18 2 18 9" />
      <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
      <rect x="6" y="14" width="12" height="8" />
    </svg>
  );

  return (
    <div style={styles.menuWrapper} ref={ref}>
      <div style={styles.dots} onClick={() => setOpen(!open)}>⋮</div>
      {open && (
        <div style={styles.menu}>
          <div style={styles.menuIconRow}>
            <div
              style={styles.menuIconBtn}
              onClick={handlePrint}
              title="Print"
            >
              <PrintIcon />
            </div>
            <div
              style={styles.menuIconBtn}
              onClick={handleDownload}
              title="Download"
            >
              {/* Download/image icon like image 2 */}
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#555" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <polyline points="21 15 16 10 5 21" />
                <path d="M7 17 l2-2 3 3 2-2 4 4" />
                <path d="M16 18 l2 -3" />
                <line x1="19" y1="16" x2="19" y2="19" />
                <line x1="17" y1="19" x2="21" y2="19" />
              </svg>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
export function TopSalesLocationDonut({
  rows = [],
  title = "Sales by Location",
  labelKey = "location",
  onDragStart,
  onDragEnd,
}) {
  const chartRef = useRef();
  const [byType, setByType] = useState("Amount");
  const [topN, setTopN] = useState("10");
  const slicedRows = sliceRowsByTopN(rows, topN, byType);
  const colors = slicedRows.map((_, i) => LOCATION_COLORS[i % LOCATION_COLORS.length]);

 const donutData = slicedRows.map((r) =>
  byType === "Amount"
    ? (Number(r.sellingPrice) || 0)
    : (Number(r.qty) || 0)
);   
  const total = donutData.reduce((s, v) => s + v, 0);

  const data = {
    labels: slicedRows.map((r) => r[labelKey]),
    datasets: [
      {
        data: donutData,
        backgroundColor: colors,
        borderWidth: 2,
        borderColor: "#fff",
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const val = ctx.parsed;
            const pct = total > 0 ? ((val / total) * 100).toFixed(1) : 0;
            return ` ${fmtCompact(val)} (${pct}%)`;
          },
        },
      },
    },
    cutout: "65%",
  };

  // Find top location
 const maxValue = donutData.length ? Math.max(...donutData) : 0;
const topIdx = donutData.indexOf(maxValue);
const topLocation = slicedRows[topIdx];
  const topPct = total > 0 ? ((donutData[topIdx] / total) * 100).toFixed(1) : 0;

  return (
    <div style={{ ...styles.card, position: "relative" }}>
      <div style={styles.cardHeader}>
        <div
          style={styles.dragHandle}
          draggable
          title="Drag to reorder chart"
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
        >
          <svg width="14" height="14" viewBox="0 0 12 12" fill="none" aria-hidden="true">
            <circle cx="3" cy="2.5" r="1" fill="#6B7280" />
            <circle cx="3" cy="6" r="1" fill="#6B7280" />
            <circle cx="3" cy="9.5" r="1" fill="#6B7280" />
            <circle cx="9" cy="2.5" r="1" fill="#6B7280" />
            <circle cx="9" cy="6" r="1" fill="#6B7280" />
            <circle cx="9" cy="9.5" r="1" fill="#6B7280" />
          </svg>
        </div>
        <p style={styles.sectionTitle}>{title}</p>
        <div style={styles.headerRight}>
          <TopNDropdown value={topN} onChange={setTopN} />
          <ByTypeDropdown value={byType} onChange={setByType} />
          <ThreeDotsMenu chartRef={chartRef} />
        </div>
      </div>

      <div style={styles.donutLayout}>
        {/* Donut */}
        <div style={styles.donutContainer}>
          <div style={{ height: 365, minHeight: 365, position: "relative" }}>
            <Doughnut ref={chartRef} data={data} options={options} />
            {/* Center label */}
            {topLocation && (
              <div style={styles.donutCenter}>
                <div style={styles.donutCenterName}>{topLocation[labelKey]}</div>
                <div style={styles.donutCenterVal}>{fmtCompact(donutData[topIdx])}</div>
                <div style={styles.donutCenterPct}>({topPct}%)</div>
              </div>
            )}
          </div>
        </div>

        {/* Legend */}
        <div style={styles.donutLegend}>
          {slicedRows.map((r, i) => {
            const val = donutData[i];
            const pct = total > 0 ? ((val / total) * 100).toFixed(1) : 0;
            return (
              <div key={i} style={styles.donutLegendItem}>
                <span style={{ ...styles.donutLegendDot, background: colors[i] }} />
                <span style={styles.donutLegendLabel}>{r[labelKey]}</span>
                <span style={styles.donutLegendValue}>{fmtCompact(val)}</span>
                <span style={styles.donutLegendPct}>{pct}%</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function LocationCharts({ rows = [] }) {
const safeRows = rows.filter((r) => r && r.location);
const countryMap = new Map();
safeRows.forEach((r) => {
  const key = r.countryName || "Unknown";
  const prev = countryMap.get(key) || { countryName: key, sellingPrice: 0, qty: 0 };
  countryMap.set(key, {
    countryName: key,
    sellingPrice: prev.sellingPrice + (Number(r.sellingPrice) || 0),
    qty: prev.qty + (Number(r.qty) || 0),
  });
});
const safeRowsCountry = Array.from(countryMap.values());
const [chartOrder, setChartOrder] = useState(["location", "country"]);
const [draggingChart, setDraggingChart] = useState(null);

const renderChart = {
  location: (cardId) => (
    <TopSalesLocationDonut
      rows={safeRows}
      title="Sales by Location"
      labelKey="location"
      onDragStart={() => handleDragStart(cardId)}
      onDragEnd={() => setDraggingChart(null)}
    />
  ),
  country: (cardId) => (
    <TopSalesLocationDonut
      rows={safeRowsCountry}
      title="Sales by Country"
      labelKey="countryName"
      onDragStart={() => handleDragStart(cardId)}
      onDragEnd={() => setDraggingChart(null)}
    />
  ),
};

function handleDragStart(cardId) {
  setDraggingChart(cardId);
}

function handleDrop(targetId) {
  if (!draggingChart || draggingChart === targetId) return;
  const next = [...chartOrder];
  const fromIdx = next.indexOf(draggingChart);
  const toIdx = next.indexOf(targetId);
  if (fromIdx < 0 || toIdx < 0) return;
  const [moved] = next.splice(fromIdx, 1);
  next.splice(toIdx, 0, moved);
  setChartOrder(next);
  setDraggingChart(null);
}

  return (
    <div style={styles.root}>
      <div style={styles.twoColRow}>
        {chartOrder.map((cardId) => (
          <div
            key={cardId}
            style={styles.colItem}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => handleDrop(cardId)}
          >
            {renderChart[cardId](cardId)}
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// 🎨 STYLES
// ─────────────────────────────────────────────────────────────
const styles = {
  root: {
    padding: "0 20px 16px",
    background:'#fff'
  },
  twoColRow: {
    display: "flex",
    gap: 12,
    marginBottom: 0,
    alignItems: "center",
    justifyContent: "center",
  },
  colItem: {
    flex: 1,
    minWidth: 0,
    position: "relative",
  },
  dragHandle: {
    cursor: "grab",
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 4,
    padding: "2px 3px",
    lineHeight: 0,
    flexShrink: 0,
  },
  card: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 6,
    padding: "14px 16px",
    marginBottom: 0,
    position: "relative",
    textAlign: "center",
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    flexWrap: "nowrap",
    marginBottom: 8,
    gap: 10,
  },
  headerRight: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 600,
     color: "#606060",
    margin: '10px 2px',
    flex: 1,
    minWidth: 0,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    textAlign: "left",
    fontFamily:'Segoe UI',
  },
  legend: {
    display: "flex",
    gap: 10,
    marginBottom: 10,
    flexWrap: "wrap",
  },
  legendItem: {
    display: "flex",
    alignItems: "center",
    gap: 5,
    fontSize: 12,
    color: "#555",
  },
  legendDot: {
    width: 10,
    height: 10,
    borderRadius: 2,
    flexShrink: 0,
  },

  // BY TYPE DROPDOWN
  byTypeWrapper: {
    position: "relative",
    fontFamily:'Segoe UI',
  },
  byTypeBtn: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    fontSize: 12,
    fontWeight: 600,
    color: "#606060",
    border: "1px solid #ddd",
    borderRadius: 6,
    padding: "4px 10px",
    cursor: "pointer",
    background: "#fff",
    whiteSpace: "nowrap",
    userSelect: "none",
    fontFamily:'Segoe UI',
  },
  byTypeChevron: {
    fontSize: 13,
    color: "#606060",
  },
  byTypeMenu: {
    position: "absolute",
    right: 0,
    top: "calc(100% + 4px)",
    background: "#fff",
    border: "1px solid #ddd",
    borderRadius: 8,
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
    width: 140,
    zIndex: 20,
    overflow: "hidden",
  },
  byTypeOption: {
    display: "flex",
    alignItems: "center",
    padding: "9px 14px",
    fontSize: 13,
    cursor: "pointer",
    color: "#333",
    gap: 4,
  },
  // TOP N DROPDOWN
  topNWrapper: { position: "relative",fontFamily:'Segoe UI', },
  topNBtn: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    fontSize: 12,
    fontWeight: 600,
   color: "#606060",
    border: "1px solid #ddd",
    borderRadius: 6,
    padding: "4px 10px",
    cursor: "pointer",
    background: "#fff",
    whiteSpace: "nowrap",
    fontFamily:'Segoe UI',
    userSelect: "none",
  },
  topNChevron: { fontSize: 14, color: "#606060" },
  topNMenu: {
    position: "absolute",
    right: 0,
    top: "calc(100% + 4px)",
    background: "#fff",
    border: "1px solid #ddd",
    borderRadius: 8,
    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
    width: 130,
    zIndex: 20,
    overflow: "hidden",
    fontFamily:'Segoe UI',
  },
  topNOption: {
    display: "flex",
    alignItems: "center",
    padding: "9px 14px",
    fontSize: 14,
    cursor: "pointer",
    color: "#606060",
    gap: 4,
    fontFamily:'Segoe UI',
  },

  // THREE DOTS MENU
  menuWrapper: {
    position: "relative",
  },
  dots: {
    cursor: "pointer",
    fontSize: 22,
    color: "#606060",
    padding: "0 4px",
    lineHeight: 1,
    userSelect: "none",
  },
  menu: {
    position: "absolute",
    right: 0,
    top: "calc(100% + 6px)",
    background: "#fff",
    border: "1px solid #e0e0e0",
    borderRadius: 10,
    boxShadow: "0 4px 16px rgba(0,0,0,0.12)",
    zIndex: 30,
    padding: "10px 12px",
  },
  menuIconRow: {
    display: "flex",
    gap: 16,
    alignItems: "center",
  },
  menuIconBtn: {
    cursor: "pointer",
    padding: 6,
    borderRadius: 6,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "background 0.15s",
  },

  // DONUT
  donutLayout: {
    display: "flex",
    gap: 16,
    alignItems: "center",
    justifyContent: "center",
  },
  donutContainer: {
    flex: 1,
    position: "relative",
  },
  donutCenter: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    textAlign: "center",
    pointerEvents: "none",
    maxWidth: 90,
  },
  donutCenterName: {
    fontSize: 9,
    color: "#888",
    lineHeight: 1.2,
    wordBreak: "break-word",
  },
  donutCenterVal: {
    fontSize: 13,
    fontWeight: 700,
    color: "#222",
  },
  donutCenterPct: {
    fontSize: 10,
    color: "#555",
  },
  donutLegend: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    gap: 5,
    maxHeight: 240,
    overflowY: "auto",
    paddingTop: 4,
  },
  donutLegendItem: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 12,
  },
  donutLegendDot: {
    width: 9,
    height: 9,
    borderRadius: "50%",
    flexShrink: 0,
  },
  donutLegendLabel: {
    flex: 1,
    color: "#444",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    textAlign: "left",
  },
  donutLegendPct: {
    color: "#888",
    fontWeight: 500,
    flexShrink: 0,
  },
  donutLegendValue: {
    color: "#4b5563",
    minWidth: 52,
    textAlign: "right",
    flexShrink: 0,
  },
};